import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "./Toast";

type Props = {
  /** path under /api, e.g. "/exports/orders.csv?status=DRAFT" */
  path: string;
  label: string;
  /** filename used by the browser; the BE Content-Disposition is the source of truth, but Safari/iOS use this */
  filename: string;
};

/**
 * Authenticated download button. Uses fetch (sends the auth cookie) +
 * blob URL so we can download even when the response sets Content-Disposition.
 * No signed query-string trick needed.
 */
export function ExportButton({ path, label, filename }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function handleClick() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`/api${path}`, { credentials: "include" });
      if (!res.ok) throw new Error(t("exportButton.failed", { status: res.status }));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      console.error(e);
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // `.btn` and not an inline style of its own: this button stands next to other buttons in
  // `.page-actions`, and a row of three in which one is 12px with a 6px radius and the others are
  // 13px with an 8px radius reads as three different kinds of action rather than one group.
  return (
    <button type="button" className="btn" onClick={handleClick} disabled={busy} aria-label={label}>
      {busy ? t("exportButton.busy") : label}
    </button>
  );
}
